import { useState } from 'react';
import { AccountStatus } from '@hungryroot/customer';
import {
  DynamicConfigs,
  DynamicConfigTypes,
  getCartSaveThresholdDynamicConfig,
  getDynamicConfigValue,
  useDynamicConfigs,
} from '@hungryroot/dynamic-configs';

import { type Cart } from '@hungryroot/rest';
import {
  maxSameItem,
  getColdVolumeLimit,
  getVolumeLimit,
} from '../utils/constants';
import {
  calculateThresholdDifference,
  cartHasChanges,
  isNotAddable,
} from '../utils/query';
import { useCartEvents } from './useCartEvents';
import { useCartInitialization } from './useCartInitialization';

type SaveThreshold =
  | {
      isBelowSaveThreshold: false;
    }
  | {
      isBelowSaveThreshold: true;
      amount: number;
    };

export type UseCartResult = {
  volumeLimit: number;
  cart: Cart;
  cartLoading: boolean;
  coldVolumeLimit: number;
  hasChanges: boolean;
  pairingCanBeAdded(data: {
    id: number;
    ingredients: Array<{
      id: number;
      amount: number;
      is_ambient: boolean;
      volume: number;
    }>;
  }): boolean;
  saveThreshold: SaveThreshold;
  productCanBeAdded(data: {
    id: number;
    is_ambient: boolean;
    volume: number;
  }): boolean;
};

export interface BootstrappingUseCartResult {
  cartLoading: true;
  cart?: undefined;
}

interface Params {
  enabled: boolean;
  accountStatus?: AccountStatus | undefined;
}

export function useCart(
  params: Params
): UseCartResult | BootstrappingUseCartResult {
  const { enabled, accountStatus } = params;
  const [unmodifiedCart, setUnmodifiedCart] = useState<Cart | null>(null);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { data: dyanmicConfigs } = useDynamicConfigs({
    name: [DynamicConfigs.RolloverMinPointsPerOrder],
  });
  const {
    data: customizeDynamicConfigs,
    isLoading: isLoadingCustomizeDynamicConfigs,
  } = useDynamicConfigs({
    name: [DynamicConfigTypes.Customize],
  });

  const cartSaveThreshold = getCartSaveThresholdDynamicConfig(
    dyanmicConfigs?.results
  );

  const cartResult = useCartInitialization(enabled);

  useCartEvents({
    onRequestSave() {
      setIsSaving(true);
    },
  });

  const { data: cart } = cartResult;
  if (cart === undefined) {
    return {
      cartLoading: true,
    };
  }

  if (
    unmodifiedCart === null ||
    (accountStatus === AccountStatus.Skipped &&
      cartHasChanges(unmodifiedCart, cart))
  ) {
    setUnmodifiedCart(cart);
  }

  const thresholdDifference = calculateThresholdDifference(
    cartSaveThreshold,
    cart
  );
  const saveThreshold: SaveThreshold =
    thresholdDifference <= 0
      ? {
          isBelowSaveThreshold: false,
        }
      : {
          isBelowSaveThreshold: true,
          amount: thresholdDifference,
        };

  const volumeLimit = isLoadingCustomizeDynamicConfigs
    ? 0
    : customizeDynamicConfigs?.results.length
      ? getDynamicConfigValue(
          customizeDynamicConfigs.results,
          DynamicConfigs.CustomizeVolumeLimit
        )
      : getVolumeLimit();
  const coldVolumeLimit = isLoadingCustomizeDynamicConfigs
    ? 0
    : customizeDynamicConfigs?.results.length
      ? getDynamicConfigValue(
          customizeDynamicConfigs.results,
          DynamicConfigs.CustomizeColdVolumeLimit
        )
      : getColdVolumeLimit();

  const context: UseCartResult = {
    cart,
    saveThreshold,
    pairingCanBeAdded(pairing: {
      id: number;
      ingredients: Array<{
        id: number;
        amount: number;
        is_ambient: boolean;
        volume: number;
      }>;
    }) {
      return !isNotAddable({
        volumeLimit: Number(volumeLimit) || 0,
        coldVolumeLimit: Number(coldVolumeLimit) || 0,
        maxSameItem,
        pairing,
        cart,
        product: undefined,
      });
    },
    productCanBeAdded(product: {
      id: number;
      is_ambient: boolean;
      volume: number;
      is_merchandized: boolean;
    }) {
      return !isNotAddable({
        volumeLimit: Number(volumeLimit) || 0,
        coldVolumeLimit: Number(coldVolumeLimit) || 0,
        maxSameItem,
        pairing: undefined,
        product,
        cart,
      });
    },
    hasChanges: cartHasChanges(unmodifiedCart, cart),
    cartLoading: isSaving,
    volumeLimit: Number(volumeLimit) || 0,
    coldVolumeLimit: Number(coldVolumeLimit) || 0,
  };
  return context;
}
